import React, { useEffect, useState } from "react";
import { JERICHO_TOKEN_KEY } from "./JerichoEasterEgg";

/**
 * JerichoGuard — backend-validated gate for the /jericho route.
 *
 * Trust model (Phase 2):
 *   - sessionStorage holds an OPAQUE reference to a backend-issued session
 *     token. The token itself authorises NOTHING client-side.
 *   - On every mount we call `GET /api/jericho/session` with the token.
 *     Backend verifies the HMAC signature, looks up the Mongo row, and
 *     checks expiry + sealed_at. Any failure → identical 401 ACCESS DENIED.
 *   - On 401 we flash a cinematic "ACCESS DENIED // NODE SEALED" CRT
 *     banner, clear the local token, and `window.location.replace("/")`.
 *
 * sessionStorage is intentionally NOT a source of truth — only of cache.
 * Closing the tab/browser also clears it so a stale reference can never
 * reach the next visitor.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FLASH_MS = 1100;

export const JerichoGuard = ({ children }) => {
  const [status, setStatus] = useState("checking"); // checking | allowed | denied
  const [operator, setOperator] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      let token = null;
      try { token = sessionStorage.getItem(JERICHO_TOKEN_KEY); }
      catch (_) { /* noop */ }
      if (!token) { if (!cancelled) setStatus("denied"); return; }
      try {
        const r = await fetch(`${API}/jericho/session`, {
          method: "GET",
          headers: { "x-jericho-token": token },
        });
        if (cancelled) return;
        if (!r.ok) {
          try { sessionStorage.removeItem(JERICHO_TOKEN_KEY); }
          catch (_) { /* noop */ }
          setStatus("denied");
          return;
        }
        const data = await r.json();
        setOperator(data);
        setStatus("allowed");
      } catch (_) {
        if (!cancelled) setStatus("denied");
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (status !== "denied") return undefined;
    const id = window.setTimeout(() => {
      window.location.replace("/");
    }, FLASH_MS);
    return () => window.clearTimeout(id);
  }, [status]);

  if (status === "allowed") {
    // Inject operator info into children via a global context-free prop
    // so the Jericho page can render the operator badge & Seal button.
    if (React.isValidElement(children)) {
      return React.cloneElement(children, { jerichoOperator: operator });
    }
    return children;
  }

  if (status === "checking") {
    // Brief verifying placeholder — never expose Jericho chrome here.
    return (
      <div
        data-testid="jericho-verifying"
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black"
      >
        <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#7a8294]">
          // VERIFYING CLEARANCE…
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="jericho-access-denied"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.10]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(to bottom, rgba(255,255,255,0.5) 0px, rgba(255,255,255,0.5) 1px, transparent 1px, transparent 3px)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.85) 100%)",
        }}
      />
      <div className="relative px-6 sm:px-10 py-8 sm:py-10 border border-[#D92D20]/60 bg-[#0a0907] shadow-[0_0_0_1px_rgba(217,45,32,0.12),0_30px_60px_rgba(0,0,0,0.8),0_0_140px_rgba(217,45,32,0.18)]">
        <div className="flex items-center gap-2 mb-3">
          <span className="block w-2 h-2 rounded-full bg-[#D92D20] animate-pulse" />
          <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#D92D20]">
            // ARCHIVE INTERNAL NODE
          </span>
        </div>
        <div
          className="font-display text-2xl sm:text-3xl text-[#E4DFD3] leading-tight"
          style={{ animation: "jerichoDenyFlash 0.42s steps(2, end) 3" }}
        >
          ACCESS DENIED
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#D92D20] mt-2">
          // NODE SEALED · CLEARANCE REQUIRED
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7a8294] mt-4">
          returning to surface…
        </div>
      </div>
      <style>{`
        @keyframes jerichoDenyFlash {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
      `}</style>
    </div>
  );
};

export default JerichoGuard;
